import {isPlatformBrowser, NgClass, NgForOf, NgIf} from '@angular/common';
import {ChangeDetectionStrategy, Component, Inject, OnInit, PLATFORM_ID} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {APP_ROUTES} from '../../../constants/routes';
import {RouteResult} from '../../../interfaces/route';
import {AnalyticsService} from '../../../services/analytics.service';
import {RoutePlannerComponent} from '../route-planner/route-planner.component';

type CostCalculationMode = 'full' | 'fuel';

interface CostPerKmPersistedState {
    mode?: CostCalculationMode;
    mileage?: number;
    fuelPrice?: number;
    consumption?: number;
    maintenance?: number;
    insuranceAndTax?: number;
    tyresAndCare?: number;
    otherExpenses?: number;
    depreciation?: number;
}

interface CostBreakdownItem {
    label: string;
    amount: number;
    tone: 'fuel' | 'maintenance' | 'fixed' | 'other' | 'depreciation';
}

const STORAGE_KEY = 'motordesk.publicCostPerKmCalculator.state.v1';

@Component({
    selector: 'app-public-cost-per-km-calculator',
    standalone: true,
    imports: [
        FormsModule,
        NgClass,
        NgForOf,
        NgIf,
        RouterLink,
        RoutePlannerComponent,
    ],
    templateUrl: './public-cost-per-km-calculator.component.html',
    styleUrl: './public-cost-per-km-calculator.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicCostPerKmCalculatorComponent implements OnInit {
    protected readonly APP_ROUTES = APP_ROUTES;
    mode: CostCalculationMode = 'full';
    mileage = 18000;
    fuelPrice = 75;
    consumption = 8;
    maintenance = 58000;
    insuranceAndTax = 24000;
    tyresAndCare = 21000;
    otherExpenses = 14000;
    depreciation = 120000;
    routeEstimatorOpen = false;
    routeResult: RouteResult | null = null;
    private readonly editedFields = new Set<keyof Omit<CostPerKmPersistedState, 'mode'>>();

    constructor(
        private analytics: AnalyticsService,
        @Inject(PLATFORM_ID) private platformId: object,
    ) {
    }

    ngOnInit(): void {
        this.restoreState();
        this.analytics.goalOnce('cost_km_view', {source: 'blog'});
    }

    selectMode(mode: CostCalculationMode): void {
        if (this.mode === mode) {
            return;
        }

        this.mode = mode;
        this.persistState();
        this.analytics.goal('cost_km_mode_selected', {source: 'blog', mode});
    }

    onValueChange(field: keyof Omit<CostPerKmPersistedState, 'mode'>, rawValue: number | string): void {
        const value = this.normalizeNonNegativeNumber(rawValue);
        this[field] = value as never;
        this.editedFields.add(field);
        this.persistState();
        this.analytics.goalOnce('cost_km_started', {source: 'blog', mode: this.mode});

        if (this.editedFields.size >= 2 && this.hasCalculation()) {
            this.analytics.goalOnce('cost_km_completed', {
                source: 'blog',
                mode: this.mode,
                depreciation: this.depreciation > 0,
            });
        }
    }

    onInputFocus(event: FocusEvent): void {
        const target = event.target;
        if (target instanceof HTMLInputElement) {
            target.select();
        }
    }

    toggleRouteEstimator(): void {
        this.routeEstimatorOpen = !this.routeEstimatorOpen;
        this.analytics.goal('cost_km_route_toggle', {
            source: 'cost_km_blog',
            open: this.routeEstimatorOpen,
            mode: this.mode,
        });
    }

    onRouteCalculated(result: RouteResult): void {
        this.routeResult = result;
        this.analytics.goal('cost_km_route_calculated', {
            source: 'cost_km_blog',
            mode: this.mode,
            distance: result.effective_distance_km,
            round_trip: result.round_trip,
            full_cost: this.routeFullCost(),
        });
    }

    onRouteInvalidated(): void {
        this.routeResult = null;
    }

    fuelCostForPeriod(): number {
        return this.roundMoney((this.mileage * this.consumption * this.fuelPrice) / 100);
    }

    fuelCostPerKm(): number {
        if (!this.hasFuelCalculation()) {
            return 0;
        }

        return this.roundMoney((this.consumption * this.fuelPrice) / 100);
    }

    operatingExpenses(): number {
        return this.roundMoney(
            this.fuelCostForPeriod()
            + this.maintenance
            + this.insuranceAndTax
            + this.tyresAndCare
            + this.otherExpenses,
        );
    }

    operatingCostPerKm(): number {
        if (!this.hasCalculation()) {
            return 0;
        }

        return this.roundMoney(this.operatingExpenses() / this.mileage);
    }

    fullExpenses(): number {
        return this.roundMoney(this.operatingExpenses() + this.depreciation);
    }

    fullCostPerKm(): number {
        if (!this.hasCalculation()) {
            return 0;
        }

        return this.roundMoney(this.fullExpenses() / this.mileage);
    }

    routePrimaryCost(): number {
        return this.mode === 'full' ? this.routeFullCost() : this.routeFuelCost();
    }

    routeFullCost(): number {
        return this.roundMoney(this.fullCostPerKm() * this.routeDistance());
    }

    routeOperatingCost(): number {
        return this.roundMoney(this.operatingCostPerKm() * this.routeDistance());
    }

    routeFuelCost(): number {
        return this.roundMoney(this.fuelCostPerKm() * this.routeDistance());
    }

    routeFuelLitres(): number {
        return this.roundMoney((this.routeDistance() * this.consumption) / 100);
    }

    routeAppliedCostPerKm(): number {
        return this.mode === 'full' ? this.fullCostPerKm() : this.fuelCostPerKm();
    }

    routeDistance(): number {
        return this.routeResult?.effective_distance_km || 0;
    }

    breakdown(): CostBreakdownItem[] {
        const items: CostBreakdownItem[] = [
            {label: 'Топливо', amount: this.fuelCostForPeriod(), tone: 'fuel'},
            {label: 'ТО и ремонт', amount: this.maintenance, tone: 'maintenance'},
            {label: 'Страховка и налог', amount: this.insuranceAndTax, tone: 'fixed'},
            {label: 'Шины и уход', amount: this.tyresAndCare, tone: 'other'},
            {label: 'Прочие расходы', amount: this.otherExpenses, tone: 'other'},
            {label: 'Амортизация', amount: this.depreciation, tone: 'depreciation'},
        ];

        return items.filter((item) => item.amount > 0);
    }

    barPercent(amount: number): number {
        const total = this.fullExpenses();
        if (total <= 0) {
            return 0;
        }

        return Math.max(3, Math.round((amount / total) * 100));
    }

    hasFuelCalculation(): boolean {
        return this.consumption > 0 && this.fuelPrice > 0;
    }

    hasCalculation(): boolean {
        return this.mileage > 0 && this.hasFuelCalculation();
    }

    registrationUrl(): string {
        return this.analytics.registrationUrl('cost_km_blog');
    }

    demoUrl(): string {
        return this.analytics.demoUrl('cost_km_blog');
    }

    trackRegistrationClick(): void {
        const params = {
            source: 'cost_km_blog',
            mode: this.mode,
            result: this.mode === 'full' ? this.fullCostPerKm() : this.fuelCostPerKm(),
        };
        this.analytics.goal('cost_km_registration_click', params);
        this.analytics.goal('registration_click', params);
    }

    trackDemoClick(): void {
        const params = {source: 'cost_km_blog', mode: this.mode};
        this.analytics.goal('cost_km_demo_click', params);
        this.analytics.goal('demo_click', params);
    }

    formatNumber(value: number, maximumFractionDigits = 0): string {
        return new Intl.NumberFormat('ru-RU', {
            maximumFractionDigits,
        }).format(value);
    }

    formatMoney(value: number, maximumFractionDigits = 0): string {
        return `${this.formatNumber(value, maximumFractionDigits)} ₽`;
    }

    private restoreState(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        try {
            const rawState = window.localStorage.getItem(STORAGE_KEY);
            if (!rawState) {
                return;
            }

            const state = JSON.parse(rawState) as CostPerKmPersistedState;
            this.mode = state.mode === 'fuel' ? 'fuel' : 'full';
            this.mileage = this.restoreNumber(state.mileage, this.mileage);
            this.fuelPrice = this.restoreNumber(state.fuelPrice, this.fuelPrice);
            this.consumption = this.restoreNumber(state.consumption, this.consumption);
            this.maintenance = this.restoreNumber(state.maintenance, this.maintenance);
            this.insuranceAndTax = this.restoreNumber(state.insuranceAndTax, this.insuranceAndTax);
            this.tyresAndCare = this.restoreNumber(state.tyresAndCare, this.tyresAndCare);
            this.otherExpenses = this.restoreNumber(state.otherExpenses, this.otherExpenses);
            this.depreciation = this.restoreNumber(state.depreciation, this.depreciation);
        } catch {
        }
    }

    private persistState(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const state: CostPerKmPersistedState = {
            mode: this.mode,
            mileage: this.mileage,
            fuelPrice: this.fuelPrice,
            consumption: this.consumption,
            maintenance: this.maintenance,
            insuranceAndTax: this.insuranceAndTax,
            tyresAndCare: this.tyresAndCare,
            otherExpenses: this.otherExpenses,
            depreciation: this.depreciation,
        };

        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch {
        }
    }

    private restoreNumber(value: number | undefined, fallback: number): number {
        return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback;
    }

    private normalizeNonNegativeNumber(value: number | string | null | undefined): number {
        const numericValue = Number(value);
        return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0;
    }

    private roundMoney(value: number): number {
        return Math.round((value + Number.EPSILON) * 100) / 100;
    }
}
